import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onboardStart, onboardComplete, checkoutStart } from "../lib/api";
import { RI_CITIES } from "../lib/ri-cities";
import { TRADES } from "../lib/trades";

/**
 * OnboardTablet — single-screen field-sales onboarding for in-person closes.
 *
 * Tuned for an iPad in landscape with the contractor sitting next to Thomas
 * (or Lanre) at a supply-house counter / Home Show booth / coffee-shop demo.
 *
 * 5 fields, all big enough for fat-finger entry, single CTA goes straight to
 * Stripe Checkout for $97/mo "NearNow AutoPilot · includes ARIA".
 *
 * No multi-step wizard. No "skip for now". One screen, one decision.
 */
const RI_CITIES_WITH_OTHER = [...RI_CITIES, "Other RI City"];

const TILE_BTN = {
  minHeight: 72, fontSize: 22, fontWeight: 700,
  border: "2px solid #0A1628", background: "#fff", color: "#0A1628",
  borderRadius: 12, padding: "0 18px", cursor: "pointer",
  letterSpacing: 1, fontFamily: "Bebas Neue, sans-serif",
  transition: "all 0.12s ease",
};
const TILE_BTN_ACTIVE = {
  ...TILE_BTN, background: "#0A1628", color: "#D4AF37",
  boxShadow: "0 6px 22px rgba(212,175,55,0.35)",
};
const INPUT_BIG = {
  width: "100%", minHeight: 72, fontSize: 24, padding: "0 22px",
  border: "2px solid #cbd0db", borderRadius: 12, background: "#fff",
  color: "#0A1628", outline: "none", boxSizing: "border-box",
};
const LABEL = {
  fontSize: 16, color: "#5a5d66", letterSpacing: 2,
  fontFamily: "Bebas Neue, sans-serif", margin: "0 0 8px",
  display: "block",
};

const OnboardTablet = () => {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [business, setBusiness] = useState("");
  const [trade, setTrade] = useState("plumbing");
  const [city, setCity] = useState("Providence");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [rep, setRep] = useState("");

  // Capture rep query param (?rep=thomas) for attribution
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const r = (params.get("rep") || "").trim().toLowerCase();
    if (r) setRep(r);
  }, []);

  const ready = name.trim() && phone.trim() && email.trim();

  const closeNow = async () => {
    setError(null);
    if (!ready) {
      setError("Name, phone, and email are required.");
      return;
    }
    setBusy(true);
    try {
      const refCode = (new URLSearchParams(window.location.search).get("ref") || "").trim().toUpperCase();
      const start = await onboardStart({
        owner_name: name.trim(), phone: phone.trim(),
        city, trade,
        source: rep ? `tablet_rep_${rep}` : "tablet_field",
        ...(refCode ? { referral_code: refCode } : {}),
      });
      const cid = start.contractor_id;
      // Persist business name + assume best defaults
      await onboardComplete({
        contractor_id: cid,
        business_name: business.trim() || null,
        zip_code: null,
        travel_radius_miles: 15,
        avg_job_value: 500,
        has_website_before_nearnow: false,
      });
      // Straight to $97 NearNow AutoPilot checkout. Founding flag honored
      // server-side if this contractor lands in a founding slot.
      const res = await checkoutStart({
        contractor_id: cid, plan: "v2_starter", email: email.trim(),
        is_founding: !!start.is_founding,
      });
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      nav(`/welcome?session=${res.session_id}&plan=growth&founding=${start.is_founding ? 1 : 0}`);
    } catch (e) {
      setError("Couldn't reach our servers. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="onboard-tablet-page"
      style={{
        background: "linear-gradient(180deg, #0A1628 0%, #0A1628 220px, #F8F9FA 220px, #F8F9FA 100%)",
        minHeight: "100vh", padding: "32px 24px 80px",
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ color: "#D4AF37", letterSpacing: 4, fontSize: 15, fontFamily: "Bebas Neue, sans-serif" }}>
          NEARNOW · AUTOPILOT IN-PERSON SIGN-UP
          {rep && <span style={{ marginLeft: 14, color: "#cbd0db" }}>· Rep: {rep.toUpperCase()}</span>}
        </div>
        <h1
          className="font-display"
          data-testid="tablet-headline"
          style={{ color: "#fff", fontSize: 64, letterSpacing: 1, lineHeight: 1.02, margin: "8px 0 4px" }}
        >
          GET YOUR PHONE ANSWERED.<br />
          <span style={{ color: "#D4AF37" }}>$97 / MONTH · FIRST MONTH FREE.</span>
        </h1>
        <p style={{ color: "#cbd0db", fontSize: 18, marginTop: 2 }}>
          One screen. One signature. ARIA is answering your missed calls by tonight.
        </p>

        {/* Form card */}
        <div
          style={{
            background: "#fff", borderRadius: 16, padding: 32, marginTop: 28,
            boxShadow: "0 24px 60px rgba(10,22,40,0.18)",
          }}
        >
          {/* Row 1 — name + business */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            <div>
              <label style={LABEL}>YOUR NAME</label>
              <input
                data-testid="tablet-name"
                style={INPUT_BIG} placeholder="Joe Plumber" autoFocus
                value={name} onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label style={LABEL}>BUSINESS NAME (OPTIONAL)</label>
              <input
                data-testid="tablet-business"
                style={INPUT_BIG} placeholder="Joe's Plumbing"
                value={business} onChange={(e) => setBusiness(e.target.value)}
              />
            </div>
          </div>

          {/* Row 2 — phone + email */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 22 }}>
            <div>
              <label style={LABEL}>BEST CALLBACK NUMBER</label>
              <input
                data-testid="tablet-phone"
                style={INPUT_BIG} placeholder="(401) 555-0100"
                type="tel" inputMode="tel"
                value={phone} onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <div>
              <label style={LABEL}>EMAIL FOR RECEIPT</label>
              <input
                data-testid="tablet-email"
                style={INPUT_BIG} placeholder="you@example.com"
                type="email" inputMode="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          {/* Row 3 — trade chooser (big tiles) */}
          <label style={{ ...LABEL, marginTop: 26 }}>WHAT DO YOU DO?</label>
          <div
            data-testid="tablet-trade-grid"
            style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}
          >
            {TRADES.map((t) => (
              <button
                key={t.code}
                data-testid={`tablet-trade-${t.code}`}
                style={trade === t.code ? TILE_BTN_ACTIVE : TILE_BTN}
                onClick={() => setTrade(t.code)}
              >
                {t.label.toUpperCase()}
              </button>
            ))}
          </div>

          {/* Row 4 — city */}
          <label style={{ ...LABEL, marginTop: 26 }}>YOUR PRIMARY CITY</label>
          <select
            data-testid="tablet-city"
            style={{ ...INPUT_BIG, appearance: "none" }}
            value={city} onChange={(e) => setCity(e.target.value)}
          >
            {RI_CITIES_WITH_OTHER.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>

          {/* Plan summary */}
          <div
            data-testid="tablet-plan-summary"
            style={{
              marginTop: 32, padding: "22px 24px", borderRadius: 12,
              background: "#0A1628", color: "#fff", display: "flex",
              alignItems: "center", justifyContent: "space-between",
            }}
          >
            <div>
              <div className="font-display" style={{ color: "#D4AF37", fontSize: 26, letterSpacing: 1.5 }}>
                NEARNOW AUTOPILOT
              </div>
              <div style={{ fontSize: 15, color: "#cbd0db", marginTop: 2 }}>
                Includes ARIA · Your AI Receptionist · 2-hour urgent escalation · Dollars-Saved weekly report
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="font-display" style={{ color: "#fff", fontSize: 52, lineHeight: 1 }}>$97</div>
              <div style={{ fontSize: 14, color: "#cbd0db" }}>/ month · cancel anytime</div>
            </div>
          </div>

          {/* CTA */}
          <button
            data-testid="tablet-close-button"
            disabled={busy || !ready}
            onClick={closeNow}
            className="btn-gold tap"
            style={{
              width: "100%", marginTop: 22, fontSize: 30,
              minHeight: 96, letterSpacing: 1.5,
            }}
          >
            {busy ? "OPENING PAYMENT…" : "START MY FREE MONTH — $97 AT DAY 31"}
          </button>

          <p style={{ textAlign: "center", marginTop: 14, fontSize: 14, color: "#5a5d66" }}>
            Card required. We don&apos;t charge today. ARIA goes live within 1 hour.
          </p>

          {error && (
            <div data-testid="tablet-error" style={{
              marginTop: 16, padding: 14, borderRadius: 8,
              background: "#fde7e7", color: "#A32D2D", fontSize: 16,
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Reassurance */}
        <p style={{ textAlign: "center", marginTop: 28, color: "#5a5d66", fontSize: 14 }}>
          Stuck? Call us at <strong>401-269-0800</strong>. A real human answers.
        </p>
      </div>
    </div>
  );
};

export default OnboardTablet;
